/**
 * v2 API contract of «Счета» (/invoices): one month's projects that need an invoice, grouped by
 * payer × currency × issuing entity, and the writes of the screen (docs/research/design2-invoices.md;
 * decisions-log V25, V26).
 */
import { z } from 'zod';
import { isPeriod } from '../dates';
import { CURRENCIES } from '../money';
import { invoiceEmailSendSchema, MAX_SEND_NOTE_LENGTH } from '../schemas/email';
import type { InvoiceEmailItem } from '../schemas/email';
import type { FxRule } from './contracts';
import type { FxEstimate } from './fxEstimate';

/** Tables of the screen, in order: «Почасовые», «Фикс в месяц» (fixed + payroll), «Фикс под ключ». */
export const INVOICE_MONTH_TABLES = ['hourly', 'fixed', 'scope'] as const;
export type InvoiceMonthTableKind = (typeof INVOICE_MONTH_TABLES)[number];

/** Who issues the document: we (own), the client in its portal (client), a payroll statement. */
export type InvoiceMonthKind = 'own' | 'client' | 'payroll';
export type InvoiceMonthBilling = 'hourly' | 'fixed' | 'scope' | 'payroll';
export type InvoiceMonthStatus = 'draft' | 'issued' | 'sent' | 'partially_paid' | 'paid' | 'cancelled';
export type InvoiceChipTone = 'success' | 'warning' | 'danger' | 'info' | 'navy' | 'neutral' | 'outline';

const periodSchema = z.string().refine(isPeriod, 'Месяц в формате ГГГГ-ММ');

export const invoiceMonthQuerySchema = z.object({
  month: periodSchema,
  issuer: z.string().min(1).default('all'),
});
export type InvoiceMonthQuery = z.infer<typeof invoiceMonthQuerySchema>;

export interface InvoiceChip {
  readonly label: string;
  readonly tone: InvoiceChipTone;
  readonly title: string;
}

/** The live invoice (draft included) that bills a project's month. */
export interface InvoiceMonthInvoice {
  readonly id: string;
  /** Our number, the client's number, or null (draft, payroll statement). */
  readonly number: string | null;
  readonly status: InvoiceMonthStatus;
  readonly kind: InvoiceMonthKind;
  /** `YYYY-MM-DD` it was last sent; null when never sent. */
  readonly sentOn: string | null;
  readonly totalMinor: number;
  readonly currency: string;
  /** Σ live payment allocations. */
  readonly paidMinor: number;
}

export interface InvoiceMonthGap {
  readonly id: string;
  readonly text: string;
}

/** Where «Закрыть пробелы» leads: the project's or entity's card (gaps block) or «Нужно дополнить». */
export type InvoiceGapTarget =
  | { readonly kind: 'project'; readonly id: string }
  | { readonly kind: 'entity'; readonly id: string }
  | { readonly kind: 'gaps' };

export interface InvoiceMenuItem {
  /** `cancel`: an issued, unpaid invoice of the month (moved from the drawer, V125). */
  readonly id: 'payer' | 'merge' | 'unmerge' | 'cancel' | 'currency';
  readonly label: string;
  readonly enabled: boolean;
  readonly title: string;
}

export interface InvoicePayerChoice {
  readonly id: string;
  readonly name: string;
}

/** What the «Кому выставить счёт» dialog needs. */
export interface InvoicePayerInfo {
  readonly currentId: string | null;
  readonly currentName: string | null;
  readonly defaultId: string | null;
  readonly defaultName: string | null;
  /** Other counterparties of the project (not archived, not the current or default one). */
  readonly choices: readonly InvoicePayerChoice[];
}

export interface InvoiceMonthProject {
  readonly projectId: string;
  readonly name: string;
  /** «→ Northbeam International Inc», «разово → …», «за сен–окт». */
  readonly note: string | null;
  readonly billing: InvoiceMonthBilling;
  readonly kind: InvoiceMonthKind;
  /** hourly: «План» of the month. */
  readonly planMinutes: number | null;
  /** hourly: hours to bill (Σ of merged months); null when not entered. */
  readonly minutes: number | null;
  /** Price in force for the month; null → «нет ставки» / «нет суммы». */
  readonly price: { readonly rateE4: number | null; readonly amountMinor: number | null; readonly currency: string } | null;
  /** Issued → the invoice's lines for the project; else the month's (and merged months') amount. */
  readonly amountMinor: number | null;
  /** In another currency before the rule's rate is out (V191): grey, at the rate of `rateDate`; final at `finalDate`'s. */
  readonly amountEstimate: FxEstimate | null;
  /** scope «Не выставлено» (V68): the latest total − lines invoiced before this invoice. */
  readonly remainingMinor: number | null;
  readonly currency: string;
  /** Billed inside next month's invoice (the month is not invoiced on its own). */
  readonly mergedInto: string | null;
  /** «В счёте окт» on a merged-away month. */
  readonly chip: InvoiceChip | null;
  readonly invoice: InvoiceMonthInvoice | null;
  /** Issued and the month's data changed afterwards (V26 «Отправить обновлённый»). */
  readonly changed: boolean;
  /** What blocks issuing (empty when ready or already issued). */
  readonly gaps: readonly InvoiceMonthGap[];
  readonly gapTarget: InvoiceGapTarget | null;
  readonly menu: readonly InvoiceMenuItem[];
  readonly payer: InvoicePayerInfo;
  /** «Валюта счёта…» (V185) while the month is not issued; null once it is. */
  readonly currencyChoice: InvoiceCurrencyChoice | null;
}

/** The month's invoice currency: chosen for the month, else the contract's, else the price's (V185). */
export interface InvoiceCurrencyChoice {
  /** Chosen for this month only; null = the default applies. */
  readonly monthCurrency: string | null;
  readonly defaultCurrency: string;
  readonly defaultSource: 'contract' | 'price';
  readonly priceCurrency: string;
  readonly fxRule: FxRule;
}

/** `recompute`: an imported invoice (M2) changed after import — its lines are rebuilt, nothing is emailed (V280). */
export type InvoiceGroupSendAction = 'issue' | 'reissue' | 'send' | 'resend' | 'recompute';

export interface InvoiceConfirm {
  readonly title: string;
  readonly text: string;
  readonly ok: string;
}

/** The navy send button of a group (exactly one of the design's four cases). */
export interface InvoiceGroupSend {
  readonly action: InvoiceGroupSendAction;
  /** Accessible name: «Отправить счёт» or «Отправить обновлённый». */
  readonly label: string;
  readonly title: string;
  readonly enabled: boolean;
  /** issue: the projects that go on the invoice. */
  readonly projectIds: readonly string[];
  /** send, resend, reissue: the invoices concerned. */
  readonly invoiceIds: readonly string[];
  /** resend and reissue ask first. */
  readonly confirm: InvoiceConfirm | null;
  /**
   * The invoice may be recorded as sent outside the app — through a portal, a messenger — with
   * the PDF that went out (V293): our own invoice that is ready, even without an email to send to.
   */
  readonly elsewhere: boolean;
  /**
   * A payroll statement (M14): nothing goes by email, the envelope only takes the statement's PDF
   * on «Другим способом» (V303) — `issue` forms the statement first, `resend` adds to a formed one.
   */
  readonly statement: boolean;
}

export interface InvoiceGroupButton {
  readonly projectId: string;
  readonly enabled: boolean;
  readonly title: string;
}

/** One invoice to be: payer × currency × issuing entity (V25). */
export interface InvoiceMonthGroup {
  readonly id: string;
  readonly clientId: string | null;
  readonly clientName: string;
  readonly currency: string;
  readonly entityId: string | null;
  /** Currency code (or entity) when the same client has several groups in the table. */
  readonly note: string | null;
  /** The projects' amounts. */
  readonly sumMinor: number;
  readonly chip: InvoiceChip | null;
  readonly preview: InvoiceGroupButton | null;
  readonly send: InvoiceGroupSend | null;
  readonly projects: readonly InvoiceMonthProject[];
}

export interface InvoiceMonthTable {
  readonly kind: InvoiceMonthTableKind;
  readonly title: string;
  readonly groups: readonly InvoiceMonthGroup[];
}

export interface InvoiceMonthView {
  readonly month: string;
  /** Date a new invoice of the month gets (V295): the day it goes out, so today. */
  readonly issueDate: string;
  readonly tables: readonly InvoiceMonthTable[];
}

// ------------------------------------------------------------------ drawer

export const invoiceDrawerQuerySchema = z.union([
  z.object({ month: periodSchema, projectId: z.string().min(1) }),
  z.object({ invoiceId: z.string().min(1) }),
]);
export type InvoiceDrawerQuery = z.infer<typeof invoiceDrawerQuerySchema>;

/** The drawer only downloads the PDF of our invoice (V125). */
export type InvoiceDrawerActionId = 'pdf';

export interface InvoiceDrawerAction {
  readonly id: InvoiceDrawerActionId;
  readonly label: string;
  readonly variant: 'ink' | 'secondary' | 'ghost';
}

/** The design's paper sheet, used where there is no template render (payroll, client invoices). */
export interface InvoicePaper {
  readonly draft: boolean;
  readonly issuerName: string;
  readonly issuerKind: string;
  readonly issuerReg: string;
  readonly issuerAddress: string;
  readonly docType: 'INVOICE' | 'PAYROLL';
  readonly number: string;
  readonly date: string;
  readonly clientName: string;
  readonly contract: string;
  readonly period: string;
  readonly due: string;
  readonly lines: readonly { readonly name: string; readonly hours: string; readonly rate: string; readonly amount: string }[];
  readonly total: string;
  readonly bankLine: string;
}

export interface InvoiceIssueItem {
  readonly label: string;
  readonly entity: string;
  readonly amountMinor: number | null;
}

export interface InvoiceDrawer {
  readonly month: string;
  readonly projectId: string;
  readonly title: string;
  readonly chip: InvoiceChip;
  readonly sub: string;
  readonly invoice: InvoiceMonthInvoice | null;
  readonly kind: InvoiceMonthKind;
  readonly currency: string;
  /** Template preview (own invoices): the iframe URL query and the template caption. */
  readonly preview: { readonly template: string; readonly draft: boolean } | null;
  readonly paper: InvoicePaper | null;
  readonly history: readonly { readonly text: string; readonly date: string }[];
  readonly actions: readonly InvoiceDrawerAction[];
  /** Projects the drawer's «Выставить…» / «Сохранить черновик» act on (the group's pending ones). */
  readonly projectIds: readonly string[];
  readonly gaps: readonly InvoiceMonthGap[];
  readonly gapTarget: InvoiceGapTarget | null;
  /** The issue dialog's rows. */
  readonly issueItems: readonly InvoiceIssueItem[];
  readonly issueDate: string;
  readonly payerId: string | null;
}

// ------------------------------------------------------------------ letter

/** GET /invoices/month/letter — the send dialog of a client group, by any of its projects. */
export const invoiceLetterQuerySchema = z.object({ month: periodSchema, projectId: z.string().min(1) });
export type InvoiceLetterQuery = z.infer<typeof invoiceLetterQuerySchema>;

/** The email that goes out with the group's invoice, filled from the entity's templates (E2, V207). */
export interface InvoiceLetterDraft {
  /** The invoice's number, or the one it gets when sent. */
  readonly number: string | null;
  /** Null while the entity has no Gmail account. */
  readonly fromEmail: string | null;
  readonly to: readonly string[];
  readonly cc: readonly string[];
  /** Every contact email of the client, offered as recipients. */
  readonly knownRecipients: readonly string[];
  readonly subject: string;
  readonly body: string;
  readonly attachmentName: string;
}

export interface InvoiceLetter {
  /** Null when the group has nothing of ours to send. */
  readonly draft: InvoiceLetterDraft | null;
  /** Invoices that go out at once; with several each gets its own letter, so the text is not editable. */
  readonly invoiceCount: number;
  /** Template of the preview tab; null without our own invoice. */
  readonly template: string | null;
  /** Every send attempt of the group's invoices, newest first. */
  readonly history: readonly InvoiceEmailItem[];
}

/** The dialog's edits: recipients always, subject and body only when changed (else re-filled at send). */
export const invoiceLetterEditSchema = invoiceEmailSendSchema.partial({ subject: true, body: true });
export type InvoiceLetterEdit = z.infer<typeof invoiceLetterEditSchema>;

// ------------------------------------------------------------------ writes

/** Every write answers with the design's toast / Activity text and an undo token when undoable. */
export interface InvoiceWriteResult {
  readonly message: string;
  readonly undoToken: string | null;
}

export const invoiceIssueSchema = z.object({
  month: periodSchema,
  projectIds: z.array(z.string().min(1)).min(1).max(100),
  /** Group button: issue and email at once; drawer: issue only. */
  send: z.boolean().default(false),
  letter: invoiceLetterEditSchema.optional(),
});
export type InvoiceIssueBody = z.infer<typeof invoiceIssueSchema>;

export const invoiceDraftSchema = z.object({
  month: periodSchema,
  projectIds: z.array(z.string().min(1)).min(1).max(100),
});
export type InvoiceDraftBody = z.infer<typeof invoiceDraftSchema>;

/**
 * Multipart fields of POST /invoices/month/sent-elsewhere (the PDF that went out in `file`,
 * optional): the group by one of its projects, as the letter is asked (V293).
 */
export const invoiceSentElsewhereSchema = z.object({
  month: periodSchema,
  projectId: z.string().min(1),
  note: z.string().trim().max(MAX_SEND_NOTE_LENGTH, `Не длиннее ${MAX_SEND_NOTE_LENGTH} символов`).optional(),
});
export type InvoiceSentElsewhereBody = z.infer<typeof invoiceSentElsewhereSchema>;

export const invoiceMonthIdsSchema = z.object({
  invoiceIds: z.array(z.string().min(1)).min(1).max(100),
  letter: invoiceLetterEditSchema.optional(),
});
export type InvoiceMonthIdsBody = z.infer<typeof invoiceMonthIdsSchema>;

export const INVOICE_CANCEL_REASON_MAX = 500;
export const invoiceCancelSchema = z.object({
  reason: z.string().trim().min(1, 'Укажите причину отмены').max(INVOICE_CANCEL_REASON_MAX),
});
export type InvoiceCancelBody = z.infer<typeof invoiceCancelSchema>;

export const invoiceMergeSchema = z.object({ month: periodSchema, projectId: z.string().min(1) });
export type InvoiceMergeBody = z.infer<typeof invoiceMergeSchema>;

export const INVOICE_PAYER_SCOPES = ['once', 'future'] as const;
export type InvoicePayerScope = (typeof INVOICE_PAYER_SCOPES)[number];
export const INVOICE_NEW_CLIENT_MAX = 200;

export const invoicePayerSchema = z.object({
  month: periodSchema,
  projectId: z.string().min(1),
  scope: z.enum(INVOICE_PAYER_SCOPES),
  target: z.union([
    z.object({ clientId: z.string().min(1) }).strict(),
    z.object({ newClientName: z.string().trim().min(1, 'Выберите контрагента').max(INVOICE_NEW_CLIENT_MAX) }).strict(),
    z.object({ reset: z.literal(true) }).strict(),
  ]),
});
export type InvoicePayerBody = z.infer<typeof invoicePayerSchema>;

/** POST /invoices/month/currency — the month's invoice currency (V185); null = back to the default. */
export const invoiceCurrencySchema = z.object({
  month: periodSchema,
  projectId: z.string().min(1),
  currency: z.enum(CURRENCIES).nullable(),
});
export type InvoiceCurrencyBody = z.infer<typeof invoiceCurrencySchema>;

